import { api } from '@/api';
import {
  readAccessDeviceCommandContext,
  type AccessDeviceCommandContext,
} from '@/api/core/access-device-command-context';
import type { AccessDeviceRegistryFilter } from '@/api/core/access-device-registry';
import { readAccessDeviceRegistry } from '@/api/core/access-device-registry';
import { coreQueryKey } from '@/api/core/authorization';
import type { CoreAccessDeviceInventoryFilter } from '@/api/core/core-access-device-inventory';
import { readCoreAccessDeviceInventory } from '@/api/core/core-access-device-inventory';
import { readNativeCatalog, readNativeProperties } from '@/api/core/native-property-read';
import {
  changePropertyApplicationSetting,
  readProperties,
  readPropertyCatalog,
  type ApplicationSettingDraft,
  type ApplicationSettingResult,
  type PropertyCatalog,
  type PropertyCatalogCursors,
  type PropertyCursor,
  type PropertyPage,
  type PropertySort,
} from '@/api/core/properties';
import { readPropertySourcePreview } from '@/api/core/property-source-preview';
import {
  readProviderIntegrationState,
  type ProviderIntegrationEnvironment,
} from '@/api/core/provider-integration-state';
import {
  readProviderLinkedData,
  type ProviderLinkedDataPageRequest,
} from '@/api/core/provider-linked-data';
import { readProviderLiveConnection } from '@/api/core/provider-live-connection';
import { readPropertyRecommendationWeight } from '@/api/core/recommendation-weight';
import {
  readWebsitePublication,
  type WebsitePublicationBinding,
} from '@/api/core/website-publication';
import type { ServiceClient } from '@/api/http/client';
import { isApiError } from '@/api/http/errors';
import { operatorConfig, operatorSession } from '@/auth/runtime';
import type { OperatorSession } from '@/auth/session';
import type { QueryClient } from '@tanstack/react-query';
import { readRoomAccessWithPermissionCheck } from '../access/room-access-read';

export interface PropertyRuntime {
  client: ServiceClient;
  session: OperatorSession;
  apiNamespace: string;
  /** Type brief rollout is independent of exact Room sale filter support. */
  nativeTypeBriefReadEnabled?: boolean;
  /** Exact room_id sales lookup rollout. */
  nativeRoomSalesRoomFilterEnabled?: boolean;
  /** Serving room-sales contract; independent of the backend sale-writing rollout. */
  salesReadEnabled?: boolean;
  /** Independent rollout gate; remain off until the serving backend revision is confirmed. */
  initialTypeBindingEnabled?: boolean;
  /** Independent rollout gate; remain off until the recommendation backend is confirmed. */
  recommendationWeightEnabled?: boolean;
  /** Draft-only Unit sale classification gate; stays off until backend serving is confirmed. */
  unitClassificationEnabled?: boolean;
  /** Local Type-wide sales transition draft; off until serving backend is verified. */
  typeSalesTransitionEnabled?: boolean;
}
export class ApplicationSettingRefreshError extends Error {
  constructor() {
    super('최신 설정을 다시 조회하지 못했습니다.');
    this.name = 'ApplicationSettingRefreshError';
  }
}
export const propertyRuntime: PropertyRuntime = {
  client: api.core,
  session: operatorSession,
  apiNamespace: operatorConfig?.coreApiUrl ?? 'unconfigured',
  nativeTypeBriefReadEnabled: true,
  nativeRoomSalesRoomFilterEnabled: true,
  // DEV task136 (7e62856) serves the read contract; permissions and allowed_actions still govern access.
  salesReadEnabled: true,
  initialTypeBindingEnabled: false,
  recommendationWeightEnabled: false,
  unitClassificationEnabled: false,
  typeSalesTransitionEnabled: false,
};
/** 지점 도메인 읽기 — 403이면 현재 세션의 지점 권한 거부로 보고한다(오래된 요청·취소는 제외). */
export async function propertyRead<T>(
  runtime: PropertyRuntime,
  generation: number,
  read: () => Promise<T>,
  signal?: AbortSignal,
  isCurrent: () => boolean = () => true,
): Promise<T> {
  try {
    return await read();
  } catch (error) {
    const session = runtime.session.getSnapshot();
    if (
      !signal?.aborted &&
      session.generation === generation &&
      session.status === 'authorized' &&
      isCurrent() &&
      isApiError(error) &&
      error.status === 403
    )
      runtime.session.reportDomainAccessDenied('property', generation);
    throw error;
  }
}
export function nativePropertiesQuery(
  runtime: PropertyRuntime,
  generation: number,
  cursor: string | null,
  enabled: boolean,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-properties'),
      'asc',
      cursor,
    ],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      propertyRead(
        runtime,
        generation,
        () => readNativeProperties(runtime.client, cursor, 'asc', signal),
        signal,
      ),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  };
}
export function nativePropertyCatalogQuery(
  runtime: PropertyRuntime,
  generation: number,
  id: string,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-property-catalog', id),
      null,
      null,
    ],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      propertyRead(
        runtime,
        generation,
        () => readNativeCatalog(runtime.client, id, null, signal),
        signal,
        isCurrent,
      ),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  };
}

export function propertiesQuery(
  runtime: PropertyRuntime,
  generation: number,
  cursor: PropertyCursor | null,
  enabled: boolean,
  sort: PropertySort | null = null,
) {
  return {
    queryKey: [...coreQueryKey(runtime.apiNamespace, generation, 'properties'), cursor, sort],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      propertyRead(
        runtime,
        generation,
        () => readProperties(runtime.client, cursor, signal, sort),
        signal,
      ),
    enabled,
    meta: { suppressGlobalError: true },
  };
}
export function propertyCatalogQuery(
  runtime: PropertyRuntime,
  generation: number,
  id: string,
  cursors: PropertyCatalogCursors,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  return {
    queryKey: [...coreQueryKey(runtime.apiNamespace, generation, 'property-catalog', id), cursors],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      propertyRead(
        runtime,
        generation,
        () => readPropertyCatalog(runtime.client, id, cursors, signal),
        signal,
        isCurrent,
      ),
    enabled,
    meta: { suppressGlobalError: true },
  };
}

/**
 * 같은 지점·같은 정렬에서 쪽(cursor)만 넘기는 두 조회인지 — 키는 `[...coreQueryKey(...), cursors]` 꼴이다.
 * 정렬이나 지점·세션이 다르면 이전 자료를 보여 주면 틀린 순서·남의 지점이 보이므로 이어 보이지 않는다.
 */
export function isSameCatalogScope(previous: readonly unknown[], next: readonly unknown[]) {
  const previousCursors = previous.at(-1) as PropertyCatalogCursors | undefined;
  const nextCursors = next.at(-1) as PropertyCatalogCursors | undefined;
  return (
    previous.length === next.length &&
    previousCursors !== undefined &&
    nextCursors !== undefined &&
    previous.slice(0, -1).every((part, index) => part === next[index]) &&
    previousCursors.sortBy === nextCursors.sortBy &&
    previousCursors.sortDirection === nextCursors.sortDirection
  );
}

/**
 * 쪽을 넘기는 카탈로그 조회 — 새 쪽이 올 때까지 이전 쪽을 화면에 둔다(`isPlaceholderData`로 알 수 있다).
 * 그렇지 않으면 쪽마다 목록이 통째로 사라졌다 돌아오고 아래 내용이 위아래로 튄다.
 */
export function propertyCatalogPageQuery(
  runtime: PropertyRuntime,
  generation: number,
  id: string,
  cursors: PropertyCatalogCursors,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  const query = propertyCatalogQuery(runtime, generation, id, cursors, enabled, isCurrent);
  return {
    ...query,
    placeholderData: (
      previous: PropertyCatalog | undefined,
      previousQuery: { queryKey: readonly unknown[] } | undefined,
    ) =>
      previousQuery && isSameCatalogScope(previousQuery.queryKey, query.queryKey)
        ? previous
        : undefined,
  };
}

export function roomOnlineAccessQuery(
  runtime: PropertyRuntime,
  generation: number,
  propertyId: string,
  roomId: string | null,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'room-online-access', propertyId),
      roomId,
    ],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!roomId) throw new Error('객실을 먼저 선택해 주세요.');
      return await readRoomAccessWithPermissionCheck(
        runtime,
        generation,
        propertyId,
        roomId,
        signal,
        isCurrent,
      );
    },
    enabled: enabled && roomId !== null,
    staleTime: 0,
    retry: false as const,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    meta: { suppressGlobalError: true },
  };
}

export function coreAccessDeviceInventoryQuery(
  runtime: PropertyRuntime,
  generation: number,
  propertyId: string,
  environment: 'dev' | 'prod',
  branchId: number,
  roomId: string,
  filter: CoreAccessDeviceInventoryFilter,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'core-access-device-inventory', propertyId),
      environment,
      branchId,
      roomId,
      filter.slotId ?? null,
      filter.cursor ?? null,
      filter.limit ?? 50,
    ],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        return await readCoreAccessDeviceInventory(
          runtime.client,
          propertyId,
          filter,
          environment,
          signal,
        );
      } catch (error) {
        const session = runtime.session.getSnapshot();
        if (
          !signal.aborted &&
          session.generation === generation &&
          session.status === 'authorized' &&
          isCurrent() &&
          isApiError(error) &&
          error.status === 403
        )
          runtime.session.reportDomainAccessDenied('property', generation);
        throw error;
      }
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false as const,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    meta: { suppressGlobalError: true },
  };
}

export function accessDeviceRegistryQuery(
  runtime: PropertyRuntime,
  generation: number,
  filter: AccessDeviceRegistryFilter,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'access-device-registry'),
      filter.cursor ?? null,
      filter.limit ?? 50,
      filter.sortBy ?? null,
      filter.sortDirection ?? null,
    ],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        return await readAccessDeviceRegistry(runtime.client, filter, signal);
      } catch (error) {
        const session = runtime.session.getSnapshot();
        if (
          !signal.aborted &&
          session.generation === generation &&
          session.status === 'authorized' &&
          isCurrent() &&
          isApiError(error) &&
          error.status === 403
        )
          runtime.session.reportDomainAccessDenied('property', generation);
        throw error;
      }
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false as const,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    meta: { suppressGlobalError: true },
  };
}

export function accessDeviceCommandContextQuery(
  runtime: PropertyRuntime,
  generation: number,
  coreDeviceId: string,
  propertyId: string | null,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  return {
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        generation,
        'access-device-command-context',
        coreDeviceId,
      ),
      propertyId,
    ],
    queryFn: ({ signal }: { signal: AbortSignal }): Promise<AccessDeviceCommandContext> =>
      propertyRead(
        runtime,
        generation,
        () =>
          readAccessDeviceCommandContext(
            runtime.client,
            coreDeviceId,
            propertyId ?? undefined,
            signal,
          ),
        signal,
        isCurrent,
      ),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false as const,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    meta: { suppressGlobalError: true },
  };
}

export function propertySourcePreviewQuery(
  runtime: PropertyRuntime,
  generation: number,
  propertyId: string,
  branchId: number,
  environment: 'dev' | 'prod',
  roomId: string | undefined,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'property-source-preview', propertyId),
      environment,
      branchId,
      roomId ?? null,
    ],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        return await readPropertySourcePreview(
          runtime.client,
          propertyId,
          roomId,
          branchId,
          environment,
          signal,
        );
      } catch (error) {
        const currentSession =
          runtime.session.getSnapshot().generation === generation &&
          runtime.session.getSnapshot().status === 'authorized';
        if (
          !signal.aborted &&
          currentSession &&
          isCurrent() &&
          isApiError(error) &&
          error.status === 403
        )
          runtime.session.reportDomainAccessDenied('property', generation);
        throw error;
      }
    },
    enabled,
    retry: false as const,
    meta: { suppressGlobalError: true },
  };
}

export function websitePublicationQuery(
  runtime: PropertyRuntime,
  generation: number,
  binding: WebsitePublicationBinding,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'website-publication', binding.propertyId),
      binding.environment,
      binding.branchId,
      binding.version,
    ],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        return await readWebsitePublication(runtime.client, binding, signal);
      } catch (error) {
        const session = runtime.session.getSnapshot();
        if (
          !signal.aborted &&
          session.generation === generation &&
          session.status === 'authorized' &&
          isCurrent() &&
          isApiError(error) &&
          error.status === 403
        )
          runtime.session.reportDomainAccessDenied('property', generation);
        throw error;
      }
    },
    enabled,
    staleTime: 0,
    retry: false as const,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    meta: { suppressGlobalError: true },
  };
}

export function propertyRecommendationWeightQuery(
  runtime: PropertyRuntime,
  generation: number,
  propertyId: string,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
  ownerScopeKey = 'standalone',
) {
  return {
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        generation,
        'property-recommendation-weight',
        propertyId,
      ),
      ownerScopeKey,
    ],
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      if (signal.aborted || runtime.session.getSnapshot().generation !== generation || !isCurrent())
        throw new DOMException('The operation was aborted.', 'AbortError');
      // A recommendation grant is narrower than Property read access. Keep its 403 local to the panel.
      return readPropertyRecommendationWeight(runtime.client, propertyId, signal);
    },
    enabled: enabled && runtime.recommendationWeightEnabled === true,
    staleTime: 30_000,
    retry: false as const,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    meta: { suppressGlobalError: true },
  };
}

export function providerIntegrationStateQuery(
  runtime: PropertyRuntime,
  generation: number,
  binding: {
    propertyId: string;
    environment: ProviderIntegrationEnvironment;
    branchId: number;
    version: number;
  },
  enabled: boolean,
  isCurrent: () => boolean = () => true,
) {
  return {
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        generation,
        'provider-integration-state',
        binding.propertyId,
      ),
      binding.environment,
      binding.branchId,
      binding.version,
    ],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        return await readProviderIntegrationState(
          runtime.client,
          binding.propertyId,
          binding.environment,
          signal,
        );
      } catch (error) {
        const session = runtime.session.getSnapshot();
        if (
          !signal.aborted &&
          session.generation === generation &&
          session.status === 'authorized' &&
          isCurrent() &&
          isApiError(error) &&
          error.status === 403
        )
          runtime.session.reportDomainAccessDenied('property', generation);
        throw error;
      }
    },
    enabled,
    staleTime: 0,
    retry: false as const,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    meta: { suppressGlobalError: true },
  };
}

/** Perform one deliberate live read; the result is never stored in the query cache. */
export async function readCurrentProviderLiveConnection(
  runtime: PropertyRuntime,
  generation: number,
  propertyId: string,
  signal: AbortSignal,
  isCurrent: () => boolean = () => true,
) {
  try {
    return await readProviderLiveConnection(runtime.client, propertyId, signal);
  } catch (error) {
    const session = runtime.session.getSnapshot();
    if (
      !signal.aborted &&
      session.generation === generation &&
      session.status === 'authorized' &&
      isCurrent() &&
      isApiError(error) &&
      error.status === 403
    )
      runtime.session.reportDomainAccessDenied('property', generation);
    throw error;
  }
}

/** Perform one deliberate live room read; never cache it or fall back to the legacy endpoint. */
export async function readCurrentProviderLinkedData(
  runtime: PropertyRuntime,
  generation: number,
  propertyId: string,
  signal: AbortSignal,
  page: ProviderLinkedDataPageRequest | undefined,
  isCurrent: () => boolean = () => true,
) {
  const sessionInstance = runtime.session;
  const capturedSession = sessionInstance.getSnapshot();
  const isRequestCurrent = () => {
    const session = sessionInstance.getSnapshot();
    return (
      !signal.aborted &&
      runtime.session === sessionInstance &&
      capturedSession.generation === generation &&
      capturedSession.status === 'authorized' &&
      session.generation === generation &&
      session.status === 'authorized' &&
      isCurrent()
    );
  };

  try {
    const data = await readProviderLinkedData(runtime.client, propertyId, page, signal);
    if (!isRequestCurrent()) throw new DOMException('The operation was aborted.', 'AbortError');
    return data;
  } catch (error) {
    if (
      runtime.session === sessionInstance &&
      isRequestCurrent() &&
      isApiError(error) &&
      error.status === 403
    )
      sessionInstance.reportDomainAccessDenied('property', generation);
    throw error;
  }
}

/** Reconcile only cached views for the confirmed property in the request's session generation. */
export async function applyApplicationSettingResult(
  client: QueryClient,
  apiNamespace: string,
  generation: number,
  requestedPropertyId: string,
  result: ApplicationSettingResult,
): Promise<void> {
  if (requestedPropertyId.toLowerCase() !== result.property_id.toLowerCase()) return;
  const propertyId = result.property_id.toLowerCase();
  await Promise.all([
    client.cancelQueries({ queryKey: coreQueryKey(apiNamespace, generation, 'properties') }),
    client.cancelQueries({
      queryKey: coreQueryKey(apiNamespace, generation, 'property-catalog', requestedPropertyId),
    }),
  ]);
  client.setQueriesData<PropertyPage>(
    { queryKey: coreQueryKey(apiNamespace, generation, 'properties') },
    (current) =>
      current && {
        ...current,
        items: current.items.map((property) =>
          property.property_id.toLowerCase() === propertyId && result.version >= property.version
            ? { ...property, application_enabled: result.enabled, version: result.version }
            : property,
        ),
      },
  );
  client.setQueriesData<PropertyCatalog>(
    {
      queryKey: coreQueryKey(apiNamespace, generation, 'property-catalog', requestedPropertyId),
    },
    (current) =>
      current && {
        ...current,
        property: {
          ...current.property,
          ...(result.version >= current.property.version
            ? { application_enabled: result.enabled, version: result.version }
            : {}),
        },
      },
  );
}

export function propertyApplicationSettingMutationOptions(
  runtime: PropertyRuntime,
  queryClient: QueryClient,
  generation: number,
  propertyId: string,
  isPropertyActive: () => boolean = () => true,
) {
  return {
    mutationKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'property-application-setting', propertyId),
    ],
    retry: false as const,
    mutationFn: async (draft: ApplicationSettingDraft) => {
      if (
        runtime.session.getSnapshot().generation !== generation ||
        runtime.session.getSnapshot().status !== 'authorized' ||
        !isPropertyActive()
      )
        throw new Error('현재 로그인 세션을 확인할 수 없습니다.');
      try {
        const result = await propertyRead(runtime, generation, () =>
          changePropertyApplicationSetting(runtime.client, propertyId, draft),
        );
        if (
          runtime.session.getSnapshot().generation !== generation ||
          runtime.session.getSnapshot().status !== 'authorized' ||
          !isPropertyActive()
        )
          throw new Error('현재 지점 또는 로그인 세션이 변경되었습니다.');
        await applyApplicationSettingResult(
          queryClient,
          runtime.apiNamespace,
          generation,
          propertyId,
          result,
        );
        return result;
      } catch (error) {
        const uncertain =
          isApiError(error) &&
          (error.status === 409 || error.kind === 'network' || error.kind === 'timeout');
        if (uncertain && runtime.session.getSnapshot().generation === generation) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: coreQueryKey(
                runtime.apiNamespace,
                generation,
                'property-catalog',
                propertyId,
              ),
            }),
            queryClient.invalidateQueries({
              queryKey: coreQueryKey(runtime.apiNamespace, generation, 'properties'),
            }),
          ]);
          const activeCatalogQueries = queryClient
            .getQueryCache()
            .findAll({
              queryKey: coreQueryKey(
                runtime.apiNamespace,
                generation,
                'property-catalog',
                propertyId,
              ),
            })
            .filter((query) => query.getObserversCount() > 0);
          if (
            runtime.session.getSnapshot().generation === generation &&
            activeCatalogQueries.some((query) => query.state.status === 'error')
          )
            throw new ApplicationSettingRefreshError();
        }
        throw error;
      }
    },
  };
}
